<template>
<div>
    <!-- 자동수집 설정 모달 — 멀티계정관리 창(CoupangAccountModal)의 계정 줄 「자동수집」 버튼이 연다.
         어느 계정인지는 props(suppliercode·companyid·email·collectAuth)로 받는다. 없으면 지금 로그인한 계정 -->
    <b-modal
        id="autoCollectSettingsModal"
        size="lg"
        :title="cid ? `자동수집 설정 · ${cid}` : '자동수집 설정'"
        ok-only
        ok-title="닫기"
        @show="onModalShow"
        @hidden="onModalHidden"
    >
        <div class="auto-collect-content">
            <!-- 계정 한 줄 — 멀티계정관리 창과 같은 저장소(확장)·같은 줄 데이터(makeRow·lightOf).
                 아이디·비밀번호는 멀티계정관리 창 하나에서만 바꾼다(이 창은 값을 들지 않는다). -->
            <div class="ac-account">
                <strong class="ac-account__id">{{ (accountRow && accountRow.cpId) || '쿠팡 아이디 없음' }}</strong>
                <span class="ac-pill" :class="credSaved ? 'ac-pill--ok' : 'ac-pill--bad'">{{ credSaved ? '비밀번호 저장됨' : '비밀번호 없음' }}</span>
                <span v-for="ch in channelPills" :key="ch.id" class="ac-pill" :class="'ac-pill--' + ch.tone" :title="ch.text">{{ ch.mark }} {{ ch.label }}<template v-if="ch.tone !== 'ok'"> {{ ch.text }}</template></span>
                <b-button size="sm" variant="outline-primary" class="ac-account__check" :disabled="accountSaving || !accountRow"
                          @click="checkAccountRow(accountRow)">{{ accountSaving ? '확인 중…' : '로그인 확인' }}</b-button>
            </div>
            <p v-if="!credSaved" class="auto-collect-notice auto-collect-notice--warn mt-2 mb-0" role="alert">
                이 계정의 쿠팡 비밀번호가 저장돼 있지 않습니다. 멀티계정관리 창의 이 계정 줄에 아이디·비밀번호를 넣고 「저장」을 눌러 주세요.
            </p>
            <p class="auto-collect-notice mt-2 mb-0">아이디·비밀번호는 멀티계정관리 창에서 바꿉니다 — 바꾸면 이 창도 같은 값을 씁니다. PC와 연결 프로그램이 켜져 있어야 수집됩니다.</p>
            <p v-if="crowdWarning" class="auto-collect-notice auto-collect-notice--warn mt-1 mb-0" role="status">{{ crowdWarning }}</p>

            <!-- 수집 항목 — 묶음 셋(로켓그로스 3 · 로켓배송 3 · 랭킹 1). 켜짐 = 그 계정 수집 권한 AND 채널 로그인.
                 꺼진 항목은 숨기지 않고 까닭 한 줄. 일정은 묶음마다 하나 -->
            <div v-for="g in itemGroups" :key="g.id" class="ac-group mt-3">
                <div class="ac-group__head">
                    <strong class="ac-group__title">{{ g.label }}</strong>
                    <span class="ac-group__need">{{ groupNeed(g) }}</span>
                    <span class="ac-group__sch">
                        일정
                        <span class="ac-chip" :class="{ 'ac-chip--off': groupSchedules[g.id].type === 'none' }">{{ formatSchedule(groupSchedules[g.id]) }}</span>
                        <b-button size="sm" variant="outline-primary" @click="toggleEditor(g.id)">{{ editing === g.id ? '닫기' : '바꾸기' }}</b-button>
                    </span>
                </div>
                <div v-if="editing === g.id" class="ac-editor">
                    <select class="form-control form-control-sm ac-editor__type" v-model="groupSchedules[g.id].type">
                        <option value="none">꺼짐</option>
                        <option value="daily">매일</option>
                        <option value="weekly">매주</option>
                    </select>
                    <template v-if="groupSchedules[g.id].type !== 'none'">
                        <select class="form-control form-control-sm ac-editor__time" v-model="groupSchedules[g.id].hour">
                            <option v-for="h in 24" :key="h-1" :value="String(h-1).padStart(2,'0')">{{String(h-1).padStart(2,'0')}}시</option>
                        </select>
                        <select class="form-control form-control-sm ac-editor__time" v-model="groupSchedules[g.id].minute">
                            <option v-for="m in [0,15,30,45]" :key="m" :value="String(m).padStart(2,'0')">{{String(m).padStart(2,'0')}}분</option>
                        </select>
                    </template>
                    <div v-if="groupSchedules[g.id].type === 'weekly'" class="day-selector">
                        <label v-for="day in weekDays" :key="day.value" class="day-label">
                            <input type="checkbox" v-model="groupSchedules[g.id].days" :value="day.value">
                            {{day.label}}
                        </label>
                    </div>
                </div>
                <div v-for="it in g.items" :key="it.id" class="ac-item" :class="{ 'ac-item--off': !itemState(it.id).on }">
                    <b-form-checkbox switch v-model="selectedItems[it.id]" :disabled="!itemState(it.id).on" class="ac-item__switch">
                        <span class="ac-item__name">{{ it.label }}</span>
                    </b-form-checkbox>
                    <small v-if="!itemState(it.id).on" class="ac-item__why">{{ itemState(it.id).reason }}</small>
                </div>
            </div>
            <div v-if="!hasAnyPermission" class="text-muted text-center py-3">
                <small>이 계정은 자동수집 가능한 권한이 없습니다.</small>
            </div>

            <!-- 아래 — 켜진 항목 수·다음 수집, 지금 수집·저장 -->
            <div class="ac-foot mt-3">
                <span class="ac-foot__sum">{{ summaryText }}</span>
                <b-button variant="outline-primary" @click="startCollectNow" :disabled="!credSaved || selectedCount === 0">지금 수집</b-button>
                <b-button variant="primary" @click="saveAutoCollectSettings" :disabled="!credSaved">저장</b-button>
            </div>
        </div>
    </b-modal>
</div>
</template>

<script>
import { askExtension, loadCoupangAccounts, findCoupangFor, checkRowLogin, lightOf, makeRow, makeSwitchConfirm, TONE_MARK } from '@/services/coupangAccountFlow';

// 같은 시각에 이보다 많은 계정이 잡혀 있으면 「약 N분 걸림」을 띄운다(계정은 하나씩 차례로 수집한다)
const CROWD_ACCOUNT_LIMIT = 4;
// 계정 하나를 전환해 수집하는 데 드는 대략의 시간(분). 실측 평균이 생기면 바꾼다
const MINUTES_PER_ACCOUNT = 5;
// 수집 항목 묶음 — 채널은 확장 AC_ITEM_CHANNEL(background.js)과 같다(랭킹추적 = Wing 로그인만 있으면 됨).
// perm = 서버 autoCollectAuthOf 의 칸(확장은 /api/autoCollect/verify 의 같은 값으로 항목을 거른다)
const ITEM_GROUPS = [
    { id: 'growth', label: '로켓그로스', channel: 'wing', items: [
        { id: 'rocketGrowthProducts', label: '상품관리', perm: 'growth' },
        { id: 'rocketGrowthSales', label: '판매현황 / SCM', perm: 'growth' },
        { id: 'rocketGrowthSalesLedger', label: '판매장부', perm: 'growth' },
    ] },
    { id: 'delivery', label: '로켓배송', channel: 'supplier', items: [
        { id: 'rocketDeliverySKU', label: '상품 SKU', perm: 'polist' },
        { id: 'rocketDeliveryOrder', label: '발주서', perm: 'polist' },
        { id: 'rocketDeliverySales', label: '판매현황', perm: 'sales' },
    ] },
    { id: 'rank', label: '랭킹추적', channel: 'wing', items: [
        { id: 'rankTracking', label: '랭킹추적', perm: 'rank' },
    ] },
];
const SCHEDULE_ITEM_KEYS = ITEM_GROUPS.flatMap(g => g.items.map(i => i.id));
const AUTH_TEXT = { growth: '로켓그로스', polist: '로켓배송 발주', sales: '로켓배송 판매현황', rank: '랭킹추적(랭킹 슬롯)' };
const CHANNELS = [
    { id: 'wing', label: 'Wing' },
    { id: 'supplier', label: 'Supplier' },
    { id: 'advertising', label: 'Ads' },
];
const DAY_TEXT = { mon: '월', tue: '화', wed: '수', thu: '목', fri: '금', sat: '토', sun: '일' };
const DAY_NUM = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
const offSchedule = () => ({ type: 'none', hour: '09', minute: '00', days: [] });
const groupOfItem = key => ITEM_GROUPS.find(g => g.items.some(i => i.id === key));

/** 일정 하나가 now 뒤 처음 도는 때(ms). 꺼짐이면 null */
function nextRunAt(sch, now) {
    if (!sch || sch.type === 'none') return null;
    for (let d = 0; d < 8; d++) {
        const t = new Date(now);
        t.setDate(t.getDate() + d);
        t.setHours(Number(sch.hour), Number(sch.minute), 0, 0);
        if (t.getTime() <= now) continue;
        if (sch.type === 'weekly' && (sch.days || []).length && !sch.days.some(x => DAY_NUM[x] === t.getDay())) continue;
        return t.getTime();
    }
    return null;
}

/** 두 일정이 같은 시각에 도는가(시:분 같고, 요일이 겹치는가) */
function sameSlot(a, b) {
    if (!a || !b || a.type === 'none' || b.type === 'none') return false;
    if (a.hour !== b.hour || a.minute !== b.minute) return false;
    if (a.type !== 'weekly' || b.type !== 'weekly') return true;
    const da = a.days || [];
    const db = b.days || [];
    if (!da.length || !db.length) return true;
    return da.some(d => db.includes(d));
}

export default {
    name: 'AutoCollectModal',
    props: {
        showLabel: {
            type: Boolean,
            default: false
        },
        // 설정할 계정. 비우면 지금 로그인한 계정(store)으로 동작한다 — 하위호환
        suppliercode: {
            type: String,
            default: ''
        },
        companyid: {
            type: String,
            default: ''
        },
        email: {
            type: String,
            default: ''
        },
        // 그 계정이 쓸 수 있는 수집 { rank, growth, polist, sales } — 서버 accountLink/list 가 준다
        collectAuth: {
            type: Object,
            default: null
        },
        // 같은 시각 겹침 경고용 — 다른 계정 판매자코드
        otherSuppliercodes: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            wasDarkBeforeModal: false,
            accountRow: null,      // 지금 쿠플러스 계정의 쿠팡 로그인 줄
            accountSaving: false,
            // 묶음마다 일정 하나 — 켠 항목이 그 묶음 일정으로 저장된다
            groupSchedules: { growth: offSchedule(), delivery: offSchedule(), rank: offSchedule() },
            editing: '',           // 일정 고치는 중인 묶음
            settingsHasCred: false, // 확장 설정 답의 «비밀번호 저장됨»(옛 확장은 id·pw 를 준다)
            selectedItems: {
                rankTracking: false,
                rocketGrowthProducts: false,
                rocketGrowthSales: false,
                rocketGrowthSalesLedger: false,
                rocketDeliverySKU: false,
                rocketDeliveryOrder: false,
                rocketDeliverySales: false
            },
            weekDays: [
                { value: 'mon', label: '월' },
                { value: 'tue', label: '화' },
                { value: 'wed', label: '수' },
                { value: 'thu', label: '목' },
                { value: 'fri', label: '금' },
                { value: 'sat', label: '토' },
                { value: 'sun', label: '일' }
            ],
            otherSchedules: [],    // 다른 계정들의 schedules (같은 시각 겹침 경고용)
            loadSeq: 0
        };
    },
    computed: {
        // props 로 계정을 받았나. 안 받았으면 지금 로그인한 계정(store)
        hasTarget() {
            return !!(this.suppliercode && this.companyid);
        },
        sc() {
            return this.hasTarget ? this.suppliercode : (this.$store.state.user.suppliercode || '');
        },
        cid() {
            return this.hasTarget ? this.companyid : (this.$store.state.user.companyid || '');
        },
        userEmail() {
            return this.hasTarget ? this.email : (this.$store.state.user.email || '');
        },
        userAuth() {
            return this.$store.state.user.auth || {};
        },
        // 다른 계정이면 서버가 준 그 계정 권한(collectAuth). 구 서버라 없으면 지금 계정 권한으로 대신한다
        targetAuth() {
            return this.hasTarget && this.collectAuth ? this.collectAuth : null;
        },
        hasRankTracking() {
            if (this.targetAuth) return this.targetAuth.rank === true;
            return (this.$store.state.user.rankslot || 0) > 0;
        },
        hasGrowth() {
            if (this.targetAuth) return this.targetAuth.growth === true;
            return this.userAuth.showgrowth === true;
        },
        hasPolist() {
            if (this.targetAuth) return this.targetAuth.polist === true;
            return this.userAuth.showpolist === true;
        },
        hasSales() {
            if (this.targetAuth) return this.targetAuth.sales === true;
            return this.userAuth.showsales === true;
        },
        hasAnyPermission() {
            return this.hasRankTracking || this.hasGrowth || this.hasPolist || this.hasSales;
        },
        itemGroups() {
            return ITEM_GROUPS;
        },
        // 그 계정 수집 권한 { rank, growth, polist, sales } — 서버 autoCollectAuthOf 와 같은 칸
        authOf() {
            return { rank: this.hasRankTracking, growth: this.hasGrowth, polist: this.hasPolist, sales: this.hasSales };
        },
        // «비밀번호 저장됨» 잣대 하나 — 경고·단추가 같이 본다(멀티계정관리 줄 값, 옛 확장은 설정 답 값)
        credSaved() {
            return !!((this.accountRow && this.accountRow.cpHasCred) || this.settingsHasCred);
        },
        channelPills() {
            if (!this.accountRow) return [];
            return CHANNELS.map((c) => {
                const l = lightOf(this.accountRow, c.id);
                return { id: c.id, label: c.label, tone: l.tone, text: l.text, mark: TONE_MARK[l.tone] || '' };
            });
        },
        // 지금 수집할 항목 = 켠 것 중 켤 수 있는 것
        runnableKeys() {
            return SCHEDULE_ITEM_KEYS.filter(k => this.selectedItems[k] && this.itemState(k).on);
        },
        selectedCount() {
            return this.runnableKeys.length;
        },
        activeScheduleCount() {
            return Object.values(this.schedules).filter(s => s.type !== 'none').length;
        },
        // 저장할 schedules — 켠 항목은 그 묶음 일정, 아니면 none
        schedules() {
            const result = {};
            SCHEDULE_ITEM_KEYS.forEach((key) => {
                const g = groupOfItem(key);
                const sch = this.groupSchedules[g.id];
                result[key] = this.selectedItems[key] && sch.type !== 'none'
                    ? { type: sch.type, hour: sch.hour, minute: sch.minute, days: [...(sch.days || [])] }
                    : offSchedule();
            });
            return result;
        },
        summaryText() {
            const now = Date.now();
            const next = Object.values(this.schedules).map(s => nextRunAt(s, now)).filter(Boolean).sort((a, b) => a - b)[0];
            let text = `켜진 항목 ${this.selectedCount}`;
            if (next) {
                const t = new Date(next);
                const day = new Date(now).toDateString() === t.toDateString() ? '오늘'
                    : new Date(now + 864e5).toDateString() === t.toDateString() ? '내일'
                        : `${t.getMonth() + 1}/${t.getDate()}`;
                text += ` · 다음 수집 ${day} ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
            }
            return text;
        },
        // 이 계정 일정과 같은 시각에 도는 계정이 많으면 경고 문구. 아니면 빈 문자열
        crowdWarning() {
            const mine = Object.values(this.schedules).filter(s => s && s.type !== 'none');
            let worst = null;
            mine.forEach((slot) => {
                const count = 1 + this.otherSchedules.filter(sch =>
                    SCHEDULE_ITEM_KEYS.some(k => sameSlot(slot, sch[k]))).length;
                if (!worst || count > worst.count) worst = { slot, count };
            });
            if (!worst || worst.count <= CROWD_ACCOUNT_LIMIT) return '';
            return `${worst.slot.hour}:${worst.slot.minute} 에 ${worst.count}개 계정이 예약돼 있어 차례로 수집하면 약 ${worst.count * MINUTES_PER_ACCOUNT}분 걸립니다. 시간을 나눠 두면 빨리 끝납니다.`;
        }
    },
    methods: {
        onModalShow() {
            // 현재 다크모드 상태 저장 후 라이트 테마로 전환
            this.wasDarkBeforeModal = document.body.classList.contains('dark');
            if (this.wasDarkBeforeModal) {
                document.body.classList.remove('dark');
            }
            this.loadAll();
        },
        /** 창을 열 때 — 확장 물음은 하나씩 차례로 한다(설정 답에는 어느 계정 것인지 표시가 없다) */
        async loadAll() {
            const seq = ++this.loadSeq;
            this.otherSchedules = [];
            try {
                await this.loadAccountRow();
                await this.loadAutoCollectSettings();
                const others = [];
                for (const sc of this.otherSuppliercodes) {
                    if (seq !== this.loadSeq) return;
                    const r = await askExtension('getAutoCollectSettings', { suppliercode: sc }, 'autoCollectSettings', 3000);
                    if (r && r.settings && r.settings.schedules) others.push(r.settings.schedules);
                }
                if (seq === this.loadSeq) this.otherSchedules = others;
            } catch (e) {
                console.error('[AutoCollectModal] 설정 불러오기 실패', e);
            }
        },
        /** 지금 쿠플러스 계정의 쿠팡 로그인 줄 — 멀티계정관리 창과 같은 저장소(확장)에서 읽는다 */
        async loadAccountRow() {
            const accounts = (await loadCoupangAccounts()) || [];
            // 멀티계정관리 창과 같은 잣대 — 판매자코드, 옛 서버면 회사ID 가 하나로 맞을 때만
            const cp = findCoupangFor(accounts, { suppliercode: this.sc, companyid: this.cid });
            this.accountRow = makeRow({
                email: this.userEmail,
                companyid: this.cid,
                suppliercode: this.sc,
                isCurrent: true,
            }, cp);
        },
        /** 로그인 확인 — 고르던 항목·일정은 그대로 두고 저장 여부·상태등만 다시 읽는다. 멀티계정관리 창도 다시 읽게 알린다 */
        async checkAccountRow(row) {
            if (this.accountSaving) return;
            this.accountSaving = true;
            try {
                await checkRowLogin(row, { confirmSwitch: makeSwitchConfirm(this) });
            } finally {
                this.accountSaving = false;
            }
            try {
                await this.loadCredFlag();
            } catch (e) {
                console.error('[AutoCollectModal] 저장 여부 다시 읽기 실패', e);
            }
            const keep = { result: row.result, message: row.message, messageBad: row.messageBad };
            await this.loadAccountRow();
            Object.assign(this.accountRow, keep);
            this.$emit('saved', { suppliercode: this.sc, confirmed: true, account: true });
        },
        /** 확장 설정 답의 «비밀번호 저장됨»만 읽는다(일정·고른 항목은 건드리지 않는다) */
        async loadCredFlag() {
            const sc = this.sc;
            const reply = await askExtension('getAutoCollectSettings', { suppliercode: sc }, 'autoCollectSettings', 5000);
            if (sc !== this.sc || !reply || !reply.settings) return;
            const s = reply.settings;
            this.settingsHasCred = !!(s.hasCredentials || (s.id && s.pw));
        },
        /** 항목을 켤 수 있나 { on, reason } — 그 계정 수집 권한 AND 비밀번호 저장 AND 채널 로그인 */
        itemState(key) {
            const g = groupOfItem(key);
            const item = g.items.find(i => i.id === key);
            if (!this.authOf[item.perm]) return { on: false, reason: `이 계정은 «${AUTH_TEXT[item.perm]}» 수집 권한이 없습니다` };
            if (!this.credSaved) return { on: false, reason: '쿠팡 비밀번호가 저장돼 있지 않습니다(멀티계정관리 창에서 저장)' };
            const row = this.accountRow;
            const ch = CHANNELS.find(c => c.id === g.channel);
            if (!row) return { on: false, reason: `${ch.label} 로그인 상태를 아직 못 읽었습니다` };
            const l = lightOf(row, g.channel);
            if (l.tone === 'ok') return { on: true, reason: '' };
            // 지금 브라우저 계정이 아니면 수집 때 그 계정으로 전환하며 다시 로그인한다(screen-command reloginPending)
            const live = row.status && row.status.domains && row.status.domains[g.channel];
            if (row.cpCurrent === false && row.cpHasCred && live && live.state === 'logged-out') return { on: true, reason: '' };
            return { on: false, reason: `${ch.label} ${l.text} — 위 「로그인 확인」을 눌러 주세요` };
        },
        groupNeed(g) {
            const pill = this.channelPills.find(p => p.id === g.channel);
            const parts = pill ? [`${pill.label} ${pill.mark || pill.text}`] : [];
            if (g.id === 'growth') parts.push(`그로스 메뉴 ${this.hasGrowth ? '✓' : '✕'}`);
            // 슬롯 수는 지금 로그인한 계정 것만 안다(다른 계정은 서버가 켬/끔만 준다)
            const slots = this.$store.state.user.rankslot || 0;
            if (g.id === 'rank' && this.cid === this.$store.state.user.companyid && slots > 0) parts.push(`랭킹 슬롯 ${slots}`);
            return parts.join(' · ');
        },
        toggleEditor(key) {
            this.editing = this.editing === key ? '' : key;
        },
        onModalHidden() {
            // 모달 닫힐 때 원래 테마 상태 복원
            if (this.wasDarkBeforeModal) {
                document.body.classList.add('dark');
            }
        },
        formatSchedule(schedule) {
            if (!schedule || schedule.type === 'none') return '꺼짐';
            const time = `${schedule.hour}:${schedule.minute}`;
            if (schedule.type === 'daily') return `매일 ${time}`;
            const days = (schedule.days || []).map(d => DAY_TEXT[d] || d).join('·');
            return days ? `${days} ${time}` : `매주 ${time}`;
        },
        // 수집 진행 알림(autoCollectStatus)은 헤더가 직접 받아 토스트로 띄운다 — 여기서 다시 넘기지 않는다
        /** 상태 알림 앞에 어느 계정인지 붙인다 */
        statusText(message) {
            return this.cid ? `[${this.cid}] ${message}` : message;
        },
        async loadAutoCollectSettings() {
            // 계정 관리에서 지웠거나 바꿨을 수 있다 — 이전 값을 들고 있지 않는다.
            // 창 하나를 여러 계정이 같이 쓴다 — 앞 계정의 선택·일정도 비운다
            this.settingsHasCred = false;
            this.editing = '';
            Object.keys(this.selectedItems).forEach((k) => { this.selectedItems[k] = false; });
            this.groupSchedules = { growth: offSchedule(), delivery: offSchedule(), rank: offSchedule() };
            const sc = this.sc;
            const reply = await askExtension('getAutoCollectSettings', { suppliercode: sc }, 'autoCollectSettings', 5000);
            if (sc !== this.sc) return; // 기다리는 사이 다른 계정 창으로 바뀌었다
            if (!reply || !reply.settings) return;
            const s = reply.settings;
            // 아이디·비밀번호는 이 창이 들지 않는다 — 쓰는 문은 계정 창 하나(구버전 확장이 pw 를 줘도 담지 않는다)
            this.settingsHasCred = !!(s.hasCredentials || (s.id && s.pw));
            // 항목마다 저장된 일정 → 켬 + 그 묶음 일정(묶음 안 일정이 서로 다르면 처음 것 · 저장하면 묶음 일정 하나로 맞춰진다)
            const take = (key, sch) => {
                if (!(key in this.selectedItems)) return;
                this.selectedItems[key] = true;
                const g = groupOfItem(key);
                if (this.groupSchedules[g.id].type === 'none') {
                    this.groupSchedules[g.id] = { type: sch.type, hour: sch.hour || '09', minute: sch.minute || '00', days: [...(sch.days || [])] };
                }
            };
            if (s.schedules) {
                SCHEDULE_ITEM_KEYS.forEach((key) => {
                    const sch = s.schedules[key];
                    if (sch && sch.type !== 'none') take(key, sch);
                });
            } else if (s.items && s.time) {
                // 옛 items+time 구조(마이그레이션)
                Object.keys(s.items).forEach((key) => {
                    if (s.items[key]) take(key, { type: 'daily', hour: s.time.hour, minute: s.time.minute, days: [] });
                });
            }
        },
        startCollectNow() {
            if (!this.credSaved) {
                alert('먼저 멀티계정관리 창에서 이 계정의 쿠팡 아이디·비밀번호를 저장해주세요.');
                return;
            }
            // "지금 수집"은 켠 항목 중 켤 수 있는 것만 바로 수집
            if (this.selectedCount === 0) {
                alert('수집할 항목을 켜주세요.');
                return;
            }
            const items = {};
            SCHEDULE_ITEM_KEYS.forEach((key) => { items[key] = this.runnableKeys.includes(key); });
            // id·pw 는 싣지 않는다(키 자체 없음) — 확장이 저장값을 쓴다(background.js startCollectNow)
            window.postMessage({
                greeting: "startCollectNow",
                settings: {
                    items: items,
                    companyid: this.cid,
                    suppliercode: this.sc
                }
            }, "*");
            this.$bvModal.hide('autoCollectSettingsModal');
            this.$emit('status-update', this.statusText('수동 수집 시작'));
        },
        async saveAutoCollectSettings() {
            if (!this.credSaved) {
                alert('먼저 멀티계정관리 창에서 이 계정의 쿠팡 아이디·비밀번호를 저장해주세요.');
                return;
            }
            if (this.activeScheduleCount === 0) {
                if (!confirm('모든 수집 스케줄이 삭제됩니다. 계속하시겠습니까?')) {
                    return;
                }
            }
            // 새로운 스케줄 구조로 저장 — id·pw 키는 빼고 보낸다. 빈 값을 실으면 10.85 앞 확장은 {...prev, ...settings} 로 저장 비번을 지운다
            const settingsToSave = {
                schedules: this.schedules,
                companyid: this.cid,
                suppliercode: this.sc
            };
            console.log('[AutoCollectModal] 저장 요청:', JSON.stringify(settingsToSave.schedules));
            console.log('[AutoCollectModal] activeScheduleCount:', this.activeScheduleCount);
            // 새 확장은 저장이 끝나면 autoCollectSettingsSaved 로 답한다. 구 확장은 답이 없다 → 시간 초과 후 예전처럼 진행
            const saved = await askExtension('saveAutoCollectSettings', { settings: settingsToSave }, 'autoCollectSettingsSaved', 5000);
            if (saved && saved.ok === false) {
                alert('자동수집 설정을 저장하지 못했습니다. 잠시 후 다시 시도해주세요.');
                return;
            }
            const msg = this.activeScheduleCount > 0
                ? `자동수집 설정이 저장되었습니다. (${this.activeScheduleCount}개 항목)`
                : '모든 수집 스케줄이 삭제되었습니다.';
            alert(msg);
            this.$emit('status-update', this.statusText(this.activeScheduleCount > 0 ? `${this.activeScheduleCount}개 항목 자동수집 설정됨` : '자동수집 해제됨'));
            this.$emit('saved', { suppliercode: this.sc, count: this.activeScheduleCount, confirmed: !!saved });
        }
    }
};
</script>

<style scoped>
.auto-collect-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
}

.auto-collect-content {
    padding: 10px;
}

.auto-collect-notice {
    font-size: 12px;
    color: #5f6368;
}

.auto-collect-notice--warn {
    color: #b06000;
}

/* 계정 한 줄 — 좁으면 알약이 다음 줄로 넘어간다(가로 스크롤 0) */
.ac-account {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    border: 1px solid #dee2e6;
    border-radius: 10px;
    padding: 10px 12px;
}

.ac-account__id {
    color: #1b1f5e;
    word-break: break-all;
}

.ac-account__check {
    margin-left: auto;
}

.ac-pill {
    font-size: 12px;
    padding: 2px 9px;
    border-radius: 999px;
    background: #f3f4f8;
    color: #5f6368;
    white-space: nowrap;
}

.ac-pill--ok {
    color: #1e8e3e;
}

.ac-pill--bad {
    color: #c0392b;
}

.ac-pill--wait {
    color: #b06000;
}

/* 묶음 카드 */
.ac-group {
    border: 1px solid #dee2e6;
    border-radius: 10px;
    overflow: hidden;
}

.ac-group__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    padding: 8px 12px;
    background: #f6f7fb;
}

.ac-group__title {
    color: #1b1f5e;
}

.ac-group__need {
    font-size: 12px;
    color: #7a8094;
}

.ac-group__sch {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
}

.ac-chip {
    color: #fff;
    background: #17a2b8;
    padding: 2px 9px;
    border-radius: 8px;
    white-space: nowrap;
}

.ac-chip--off {
    color: #17a2b8;
    background: #fff;
    border: 1px solid #17a2b8;
}

.ac-editor {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border-top: 1px solid #dee2e6;
}

.ac-editor__type {
    width: 80px;
}

.ac-editor__time {
    width: 76px;
}

.ac-item {
    padding: 8px 12px;
    border-top: 1px solid #dee2e6;
}

.ac-item--off .ac-item__name {
    color: #b0b5c3;
}

.ac-item__why {
    display: block;
    color: #c0392b;
    margin-left: 2.5rem;
    word-break: keep-all; /* 한글 낱말 가운데서 끊지 않는다(390 «없습/니다») */
}

.ac-foot {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.ac-foot__sum {
    margin-right: auto;
    font-size: 13px;
    color: #7a8094;
}

@media (max-width: 500px) {
    .ac-group__sch {
        margin-left: 0;
        width: 100%;
    }
    .ac-foot__sum {
        width: 100%;
    }
}

.day-selector {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
}

.day-label {
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 3px;
    font-size: 0.8rem;
    cursor: pointer;
    margin: 0;
}

.day-label input {
    margin-right: 3px;
}

.day-label:hover {
    background: #e9ecef;
}
</style>

